// Microsoft authentication (MSAL) + Microsoft Graph profile loading.
// Faithfully reconstructed from the deployed build — same config, scopes,
// cache keys, and Graph queries.

import {
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";

// --- MSAL configuration (from the deployed build) ---
const CLIENT_ID = "4f4e1e8d-0e2c-4398-9517-16fd5ae0782e";
const TENANT_ID = "95565ff3-144c-44e7-b5f4-26ccc44a16b7";
const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;
const SCOPES = ["User.Read", "User.Read.All"];

// Profile cache version — bump to invalidate cached sessionStorage profile.
const PROFILE_CACHE_VERSION = "v4";

let instance: PublicClientApplication | null = null;

export async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!instance) {
    instance = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: window.location.origin + "/auth.html",
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
    await instance.initialize();
  }
  return instance;
}

export interface SilentAuthResult {
  authed: boolean;
  account?: AccountInfo;
}

export async function trySilentAuth(): Promise<SilentAuthResult> {
  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return { authed: false };
    await msal.acquireTokenSilent({ scopes: SCOPES, account: accounts[0] });
    sessionStorage.setItem("kd_authed", "1");
    return { authed: true, account: accounts[0] };
  } catch {
    sessionStorage.removeItem("kd_authed");
    return { authed: false };
  }
}

export async function loginPopup(): Promise<AccountInfo | null> {
  const result = await (await getMsalInstance()).loginPopup({ scopes: SCOPES });
  if (result.account) {
    sessionStorage.setItem("kd_authed", "1");
    return result.account;
  }
  return null;
}

export async function msalLogout(): Promise<void> {
  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();
    sessionStorage.removeItem("kd_authed");
    sessionStorage.removeItem("kd_user");
    sessionStorage.removeItem("kd_photo");
    sessionStorage.removeItem("kd_email");
    if (accounts.length > 0) await msal.clearCache();
  } catch {
    /* ignore */
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return null;
    return (await msal.acquireTokenSilent({ scopes: SCOPES, account: accounts[0] })).accessToken;
  } catch {
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  try {
    const msal = await getMsalInstance();
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return null;
    return (await msal.acquireTokenSilent({ scopes: SCOPES, account: accounts[0] })).idToken || null;
  } catch {
    return null;
  }
}

// --- Microsoft Graph helpers ---

async function graphJson(url: string, token: string, timeoutMs = 5000, extraHeaders?: Record<string, string>): Promise<any | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, ...(extraHeaders || {}) },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      console.warn(`[graph] ${res.status} ${url}`, body.slice(0, 300));
      return null;
    }
    return await res.json();
  } catch {
    return null;
  }
}

async function graphPhoto(url: string, token: string, timeoutMs = 5000): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await resizePhoto(blob);
  } catch {
    return null;
  }
}

// Downscale a photo blob to <=128px and return a JPEG data URL (matches build).
function resizePhoto(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            if (!w || !h) return resolve(reader.result as string);
            const scale = Math.min(1, 128 / Math.max(w, h));
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(w * scale));
            canvas.height = Math.max(1, Math.round(h * scale));
            const ctx = canvas.getContext("2d", { alpha: false });
            if (!ctx) return resolve(reader.result as string);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.82));
          } catch {
            resolve(reader.result as string);
          }
        };
        img.onerror = () => resolve(reader.result as string);
        img.src = reader.result as string;
      } catch {
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export interface Person {
  name: string;
  email: string;
  title: string;
  photo: string | null;
}

export interface UserProfile {
  name: string;
  email: string;
  role: string;
  photo: string | null;
  phone: string;
  manager: string;
  managerEmail: string;
  managerPerson: Person | null;
  managerChain: string[];
  peers: string[];
  peerPersons: Person[];
}

/**
 * Load the signed-in user's profile from Microsoft Graph: identity, manager
 * chain (up to 3 levels), and peers (manager's direct reports, else same
 * department). Results are cached in sessionStorage under `kd_user`.
 */
export async function fetchUserProfile(token: string): Promise<UserProfile> {
  const cachedUser = sessionStorage.getItem("kd_user");
  const cachedPhoto = sessionStorage.getItem("kd_photo");
  if (cachedUser && cachedPhoto !== null) {
    try {
      const u = JSON.parse(cachedUser);
      if (u._v === PROFILE_CACHE_VERSION) {
        return {
          name: u.name || "",
          email: u.email || "",
          role: u.role || "",
          photo: cachedPhoto,
          phone: u.phone || "",
          manager: u.manager || "",
          managerEmail: u.managerEmail || "",
          managerPerson: u.managerPerson || null,
          managerChain: Array.isArray(u.managerChain) ? u.managerChain : [],
          peers: Array.isArray(u.peers) ? u.peers : [],
          peerPersons: Array.isArray(u.peerPersons) ? u.peerPersons : [],
        };
      }
    } catch {
      /* ignore */
    }
  }

  let name = "";
  let email = "";
  let role = "";
  let phone = "";
  let photo: string | null = null;

  const me = await graphJson(
    "https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,jobTitle,department,mobilePhone,businessPhones",
    token,
  );
  if (me && typeof me.displayName === "string") {
    name = me.displayName;
    email = me.mail || me.userPrincipalName || "";
    role = [me.jobTitle, me.department].filter((x: unknown) => typeof x === "string" && x.trim().length > 0).join(" | ");
    phone = me.mobilePhone || (me.businessPhones && me.businessPhones[0]) || "";
  }

  const managerChain: string[] = [];
  let managerPerson: Person | null = null;
  let managerEmail = "";

  const manager = await graphJson(
    "https://graph.microsoft.com/v1.0/me/manager?$select=id,displayName,mail,userPrincipalName,jobTitle",
    token,
  );
  if (manager && typeof manager.displayName === "string" && manager.displayName.trim()) {
    managerChain.push(manager.displayName);
    managerEmail = manager.mail || manager.userPrincipalName || "";
    const mgrPhoto = manager.id ? await graphPhoto(`https://graph.microsoft.com/v1.0/users/${manager.id}/photo/$value`, token) : null;
    managerPerson = { name: manager.displayName, email: managerEmail, title: manager.jobTitle || "", photo: mgrPhoto };
  }

  for (const path of ["me/manager/manager", "me/manager/manager/manager"]) {
    const node = await graphJson(`https://graph.microsoft.com/v1.0/${path}?$select=displayName`, token);
    const dn = node && typeof node.displayName === "string" ? node.displayName.trim() : "";
    if (!dn || managerChain[managerChain.length - 1] === dn) break;
    managerChain.push(dn);
  }

  const peerPersons: Person[] = [];
  const peers: string[] = [];
  const department = typeof me?.department === "string" ? me.department.trim() : "";
  const myId = typeof me?.id === "string" ? me.id : "";

  async function collectPeers(list: any[]) {
    const filtered = list
      .filter((p) => p && typeof p.displayName === "string" && p.displayName.trim())
      .filter((p) => p.displayName !== name)
      .filter((p) => !myId || p.id !== myId)
      .filter((p) => !managerEmail || (p.mail || p.userPrincipalName || "").toLowerCase() !== managerEmail.toLowerCase())
      .slice(0, 20);
    const photos = await Promise.all(
      filtered.map((p) => (p.id ? graphPhoto(`https://graph.microsoft.com/v1.0/users/${p.id}/photo/$value`, token) : Promise.resolve(null))),
    );
    for (let i = 0; i < filtered.length; i++) {
      const p = filtered[i];
      peerPersons.push({ name: p.displayName, email: p.mail || p.userPrincipalName || "", title: p.jobTitle || "", photo: photos[i] || null });
      peers.push(p.displayName);
    }
  }

  const directReports = await graphJson(
    "https://graph.microsoft.com/v1.0/me/manager/directReports?$select=id,displayName,mail,userPrincipalName,jobTitle&$top=20",
    token,
  );
  if (directReports && Array.isArray(directReports.value) && directReports.value.length > 0) {
    await collectPeers(directReports.value);
  }
  if (peerPersons.length === 0 && department) {
    const filter = `department eq '${department.replace(/'/g, "''")}'`;
    const query = [
      `$filter=${encodeURIComponent(filter)}`,
      `$select=${encodeURIComponent("id,displayName,mail,userPrincipalName,jobTitle")}`,
      "$top=30",
    ].join("&");
    const byDept = await graphJson(`https://graph.microsoft.com/v1.0/users?${query}`, token);
    if (byDept && Array.isArray(byDept.value)) await collectPeers(byDept.value);
  }

  photo = await graphPhoto("https://graph.microsoft.com/v1.0/me/photo/$value", token);

  const manager0 = managerChain[0] || "";
  try {
    sessionStorage.setItem(
      "kd_user",
      JSON.stringify({ _v: PROFILE_CACHE_VERSION, name, email, role, phone, manager: manager0, managerEmail, managerPerson, managerChain, peers, peerPersons }),
    );
  } catch {
    /* ignore */
  }
  try {
    if (email) sessionStorage.setItem("kd_email", email);
  } catch {
    /* ignore */
  }
  try {
    if (photo) sessionStorage.setItem("kd_photo", photo);
  } catch {
    /* ignore */
  }

  return { name, email, role, photo, phone, manager: manager0, managerEmail, managerPerson, managerChain, peers, peerPersons };
}
